import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Calendar, Clock, ArrowRight, Filter, BookOpen, Tag } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { posts, categories } from '@/data/posts';

const readTime = (content = '') =>
  Math.max(1, Math.ceil(content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200));

const PostCard = ({ post, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.07, duration: 0.45 }}
    className="group relative flex flex-col bg-[#0f1923] border border-white/6 rounded-2xl overflow-hidden hover:border-emerald-500/30 transition-all duration-400 shadow-lg hover:shadow-emerald-500/10 hover:shadow-2xl hover:-translate-y-1"
  >
    {/* Image placeholder */}
    <div className="relative h-52 overflow-hidden bg-emerald-900/40 flex-shrink-0">
      {post.featured_image ? (
        <img
          src={post.featured_image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <BookOpen size={40} className="text-emerald-700/60" />
        </div>
      )}
      {post.category && (
        <span className="absolute top-3 left-3 px-3 py-1 bg-black/60 backdrop-blur-sm text-emerald-400 text-xs font-bold rounded-full border border-emerald-500/20">
          {post.category.name}
        </span>
      )}
    </div>

    {/* Content */}
    <div className="flex flex-col flex-1 p-6 gap-3">
      <h3 className="text-lg font-bold text-white leading-snug line-clamp-2 group-hover:text-emerald-400 transition-colors duration-200">
        {post.title}
      </h3>
      {post.excerpt && (
        <p className="text-sm text-gray-400 line-clamp-3 flex-1 leading-relaxed">
          {post.excerpt}
        </p>
      )}
      {post.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {post.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/8 text-gray-500 text-xs">
              <Tag size={10} />
              {tag}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-white/6 mt-auto">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {new Date(post.published_at || post.created_at).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            })}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {readTime(post.content)} min
          </span>
        </div>
        <Link
          to={`/blog/${post.slug}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 hover:text-emerald-300 group/link"
        >
          Read
          <ArrowRight size={13} className="group-hover/link:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  </motion.article>
);

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');

  const filtered = useMemo(() => {
    let result = posts;
    if (selectedCategory) {
      result = result.filter((p) => p.category?.slug === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }
    return result;
  }, [searchQuery, selectedCategory]);

  const handleSearch = (val) => {
    setSearchQuery(val);
    const params = new URLSearchParams();
    if (val) params.set('search', val);
    if (selectedCategory) params.set('category', selectedCategory);
    setSearchParams(params);
  };

  const handleCategory = (val) => {
    setSelectedCategory(val);
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (val) params.set('category', val);
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-[#080d14] text-white pt-20">
      <SEO
        title="Blog — AI, Dev & Tech Insights by Tboye"
        description="Articles on AI in Africa, full-stack development, Cloudflare, entrepreneurship, and tech from Emmanuel Tete Boye."
        image="/images/image1.jpg"
        url="/blog"
      />

      {/* Hero */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-emerald-500/8 rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center space-y-4">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/8 text-emerald-400 text-xs font-semibold tracking-widest uppercase">
            <BookOpen size={13} /> Blog
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight">
            Thoughts &{' '}
            <span className="text-emerald-400">
              Insights
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Deep-dives on AI in Africa, full-stack development, Cloudflare, and building products people actually use.
          </p>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="px-4 mb-10">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/4 border border-white/8 rounded-2xl p-5 backdrop-blur-sm">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/8 focus:border-emerald-500/50 rounded-xl text-white placeholder-gray-600 text-sm outline-none transition-colors"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={16} />
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategory(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/8 focus:border-emerald-500/50 rounded-xl text-white text-sm outline-none appearance-none cursor-pointer transition-colors"
                >
                  <option value="" className="bg-gray-900">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.slug} className="bg-gray-900">
                      {cat.name} ({cat.post_count})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="px-4 pb-24">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-24 space-y-4">
              <div className="w-20 h-20 bg-white/4 rounded-full flex items-center justify-center mx-auto">
                <BookOpen size={32} className="text-gray-600" />
              </div>
              <p className="text-2xl font-semibold text-gray-400">No articles found</p>
              <button
                onClick={() => { handleSearch(''); handleCategory(''); }}
                className="text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => (
                <PostCard key={post.id} post={post} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
